import { useCallback, useEffect, useState } from "react"
import type { Player } from "~/utils/sports-wordle-data"
import { allPlayers, getAllPlayerNames, heightToInches, yearsOverlap } from "~/utils/sports-wordle-data"
import { nbaPlayers } from "~/utils/sports-wordle-players"

export type GameMode = "basketball" | "soccer" | "all"
export type PlayerMode = "current" | "retired" | "all"

export interface AttributeComparison {
  team: "correct" | "wrong"
  position: "correct" | "wrong"
  jerseyNumber: "correct" | "close" | "wrong"
  height: "correct" | "close" | "wrong"
  conference: "correct" | "wrong"
  yearsActive: "correct" | "close" | "wrong"
}

export interface GuessedPlayer {
  player: Player
  comparison: AttributeComparison
}

export interface GameState {
  targetPlayer: Player | null
  guesses: GuessedPlayer[]
  gameStatus: "playing" | "won" | "lost"
  currentInput: string
  showAutocomplete: boolean
  autocompleteResults: string[]
}

const MAX_GUESSES = 6

export function useSportsWordleGame() {
  const [gameMode, setGameMode] = useState<GameMode>("basketball")
  const [playerMode, setPlayerMode] = useState<PlayerMode>("all")
  const [selectedLeague, setSelectedLeague] = useState<string | undefined>(undefined)
  const [gameState, setGameState] = useState<GameState>({
    targetPlayer: null,
    guesses: [],
    gameStatus: "playing",
    currentInput: "",
    showAutocomplete: false,
    autocompleteResults: [],
  })
  const [showStats, setShowStats] = useState(false)
  const [showRules, setShowRules] = useState(false)
  const [streak, setStreak] = useState(0)
  const [gamesPlayed, setGamesPlayed] = useState(0)
  const [gamesWon, setGamesWon] = useState(0)

  // Get player pool based on settings
  const getPlayerPool = useCallback(() => {
    let pool = gameMode === "basketball" ? nbaPlayers : allPlayers

    if (gameMode !== "all" && gameMode !== "basketball") {
      pool = pool.filter((p) => p.sport === gameMode)
    }

    if (playerMode === "current") {
      pool = pool.filter((p) => !p.isRetired)
    } else if (playerMode === "retired") {
      pool = pool.filter((p) => p.isRetired)
    }

    if (selectedLeague && gameMode === "soccer") {
      pool = pool.filter((p) => p.league === selectedLeague)
    }

    return pool
  }, [gameMode, playerMode, selectedLeague])

  // Initialize new game
  const startNewGame = useCallback(() => {
    const pool = getPlayerPool()
    if (pool.length === 0) return

    const randomPlayer = pool[Math.floor(Math.random() * pool.length)]

    setGameState({
      targetPlayer: randomPlayer,
      guesses: [],
      gameStatus: "playing",
      currentInput: "",
      showAutocomplete: false,
      autocompleteResults: [],
    })
    setShowStats(false)
  }, [getPlayerPool])

  // Start new game on mount and when settings change
  useEffect(() => {
    startNewGame()
  }, [startNewGame])

  // Compare two players and return attribute comparison
  const comparePlayer = useCallback((guessPlayer: Player, targetPlayer: Player): AttributeComparison => {
    const comparison: AttributeComparison = {
      team: guessPlayer.team === targetPlayer.team ? "correct" : "wrong",
      position: guessPlayer.position === targetPlayer.position ? "correct" : "wrong",
      conference: guessPlayer.conference === targetPlayer.conference ? "correct" : "wrong",
      jerseyNumber: "wrong",
      height: "wrong",
      yearsActive: "wrong",
    }

    // Jersey number comparison
    if (guessPlayer.jerseyNumber && targetPlayer.jerseyNumber) {
      const diff = Math.abs(guessPlayer.jerseyNumber - targetPlayer.jerseyNumber)
      if (diff === 0) {
        comparison.jerseyNumber = "correct"
      } else if (diff <= 5) {
        comparison.jerseyNumber = "close"
      }
    }

    // Height comparison
    if (guessPlayer.height && targetPlayer.height) {
      const guessInches = heightToInches(guessPlayer.height)
      const targetInches = heightToInches(targetPlayer.height)
      const diff = Math.abs(guessInches - targetInches)
      if (diff === 0) {
        comparison.height = "correct"
      } else if (diff <= 2) {
        comparison.height = "close"
      }
    }

    // Years active comparison
    if (yearsOverlap(guessPlayer, targetPlayer)) {
      if (guessPlayer.yearsActive === targetPlayer.yearsActive) {
        comparison.yearsActive = "correct"
      } else {
        comparison.yearsActive = "close"
      }
    }

    return comparison
  }, [])

  // Update autocomplete results
  const updateAutocomplete = useCallback(
    (input: string) => {
      if (input.length < 2) {
        setGameState((prev) => ({
          ...prev,
          currentInput: input,
          showAutocomplete: false,
          autocompleteResults: [],
        }))
        return
      }

      const pool = getPlayerPool()
      const playerNames = pool.map((p) => p.fullName)
      const filtered = playerNames.filter((name) => name.toLowerCase().includes(input.toLowerCase())).slice(0, 5)

      setGameState((prev) => ({
        ...prev,
        currentInput: input,
        showAutocomplete: filtered.length > 0,
        autocompleteResults: filtered,
      }))
    },
    [getPlayerPool],
  )

  // Submit guess
  const submitGuess = useCallback(
    (playerName: string) => {
      if (gameState.gameStatus !== "playing" || !gameState.targetPlayer) return

      const pool = getPlayerPool()
      const guessPlayer = pool.find((p) => p.fullName === playerName)

      if (!guessPlayer) {
        // Invalid player name
        return
      }

      const comparison = comparePlayer(guessPlayer, gameState.targetPlayer)
      const newGuess: GuessedPlayer = { player: guessPlayer, comparison }
      const newGuesses = [...gameState.guesses, newGuess]

      let newStatus: "playing" | "won" | "lost" = gameState.gameStatus

      // Check if won
      const isCorrect = Object.values(comparison).every((v) => v === "correct")
      if (isCorrect) {
        newStatus = "won"
        setGamesWon((prev) => prev + 1)
        setStreak((prev) => prev + 1)
        setGamesPlayed((prev) => prev + 1)
        setShowStats(true)
      } else if (newGuesses.length >= MAX_GUESSES) {
        newStatus = "lost"
        setStreak(0)
        setGamesPlayed((prev) => prev + 1)
        setShowStats(true)
      }

      setGameState((prev) => ({
        ...prev,
        guesses: newGuesses,
        gameStatus: newStatus,
        currentInput: "",
        showAutocomplete: false,
        autocompleteResults: [],
      }))
    },
    [gameState, getPlayerPool, comparePlayer],
  )

  // Select from autocomplete
  const selectAutocomplete = useCallback(
    (playerName: string) => {
      submitGuess(playerName)
    },
    [submitGuess],
  )

  return {
    // Game state
    gameState,
    gameMode,
    playerMode,
    selectedLeague,

    // Stats
    showStats,
    showRules,
    streak,
    gamesPlayed,
    gamesWon,
    winRate: gamesPlayed > 0 ? Math.round((gamesWon / gamesPlayed) * 100) : 0,

    // Actions
    setGameMode,
    setPlayerMode,
    setSelectedLeague,
    startNewGame,
    submitGuess,
    updateAutocomplete,
    selectAutocomplete,
    setShowStats,
    setShowRules,

    // Constants
    maxGuesses: MAX_GUESSES,
  }
}
